import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TimelineGroup from './timeline/TimelineGroup';
import TimelineEventCard from './timeline/TimelineEventCard';
import AttendanceCluster from './timeline/AttendanceCluster';
import VirtualisedList from './timeline/VirtualisedList';
import Badge from './ui/Badge';
import Button from './ui/Button';
import {
  buildSafeguardingSignals,
  buildTimelineEntries,
  clusterAttendance,
  groupEventsByTime
} from './timeline/timelineUtils';

const FILTER_STORAGE_KEY = 'safeguardpro_timeline_filters_v1';
const EVENT_TYPES = ['All', 'Attendance', 'Safeguarding', 'Incident', 'Medical', 'Police'];
const RISK_LEVELS = ['All', 'Critical', 'Medium', 'Low'];

const loadFilters = () => {
  if (typeof window === 'undefined') {
    return { type: 'All', risk: 'All' };
  }
  try {
    const saved = JSON.parse(window.localStorage.getItem(FILTER_STORAGE_KEY) || '{}');
    return {
      type: saved.type || 'All',
      risk: saved.risk || 'All'
    };
  } catch (error) {
    return { type: 'All', risk: 'All' };
  }
};

export default function TimelineView({ events = [], isLoading = false, error = '' }) {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState(() => loadFilters().type);
  const [riskFilter, setRiskFilter] = useState(() => loadFilters().risk);
  const [showEarlier, setShowEarlier] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({ type: typeFilter, risk: riskFilter })
    );
  }, [typeFilter, riskFilter]);

  const normalized = useMemo(
    () =>
      buildTimelineEntries(events).map((event) => ({
        ...event,
        links:
          event.category === 'Incident' && event.sourceId
            ? [
                {
                  label: 'View incident',
                  onClick: () => navigate(`/incidents/${event.sourceId}`)
                }
              ]
            : []
      })),
    [events, navigate]
  );

  const filtered = useMemo(() => {
    return normalized.filter((event) => {
      const typeMatches = typeFilter === 'All' || event.category === typeFilter;
      const riskMatches =
        riskFilter === 'All' || (event.risk || 'low') === riskFilter.toLowerCase();
      return typeMatches && riskMatches;
    });
  }, [normalized, typeFilter, riskFilter]);

  const activeFilters = useMemo(() => {
    const list = [];
    if (typeFilter !== 'All') {
      list.push({ id: 'type', label: `Type: ${typeFilter}`, onRemove: () => setTypeFilter('All') });
    }
    if (riskFilter !== 'All') {
      list.push({ id: 'risk', label: `Risk: ${riskFilter}`, onRemove: () => setRiskFilter('All') });
    }
    return list;
  }, [typeFilter, riskFilter]);

  const grouped = useMemo(() => groupEventsByTime(filtered), [filtered]);
  const signals = useMemo(() => {
    if (typeFilter !== 'All' && typeFilter !== 'Attendance') return [];
    if (riskFilter !== 'All' && riskFilter !== 'Medium' && riskFilter !== 'Critical') return [];
    return buildSafeguardingSignals(filtered);
  }, [filtered, typeFilter, riskFilter]);

  const todayItems = useMemo(() => clusterAttendance(grouped.today), [grouped.today]);
  const weekItems = useMemo(() => clusterAttendance(grouped.week), [grouped.week]);
  const earlierItems = useMemo(() => clusterAttendance(grouped.earlier), [grouped.earlier]);

  const renderItem = (item) => {
    if (item.type === 'attendance-cluster') {
      return <AttendanceCluster cluster={item} />;
    }
    if (item.type === 'signal') {
      return (
        <div
          className={`sp-alert ${item.risk === 'critical' ? 'sp-alert--critical' : 'sp-alert--high'} text-sm sp-timeline-signal`}
          role="status"
        >
          <strong>Safeguarding signal:</strong> {item.label}
        </div>
      );
    }
    return <TimelineEventCard event={item} />;
  };

  if (isLoading) {
    return <div className="text-sm sp-muted">Loading timeline…</div>;
  }
  if (error) {
    return <div className="sp-alert sp-alert--critical text-sm">{error}</div>;
  }

  if (!events.length) {
    return <div className="text-sm sp-muted">No timeline events available.</div>;
  }

  const hasActiveFilters = activeFilters.length > 0;

  const buildGroupItems = (items, includeSignals = false) => {
    if (!includeSignals || !signals.length) return items;
    return [
      ...signals.map((signal) => ({ ...signal, type: 'signal' })),
      ...items
    ];
  };

  const renderGroupList = (items, includeSignals = false) => {
    const listItems = buildGroupItems(items, includeSignals);
    if (!listItems.length) {
      return null;
    }
    if (listItems.length > 18) {
      return (
        <VirtualisedList
          items={listItems}
          estimatedItemHeight={120}
          height={520}
          renderItem={renderItem}
        />
      );
    }
    return (
      <div className="space-y-3">
        {listItems.map((item) => (
          <div key={item.id || item.label}>{renderItem(item)}</div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="sp-section-header sp-section-header--surface flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Timeline</h3>
          <p className="text-xs sp-muted">
            {filtered.length} of {normalized.length} events shown - grouped by time.
          </p>
        </div>
      </div>

      <div className="sp-filter-bar sp-filter-bar--inline" aria-label="Timeline filters">
        <div className="sp-filter-main sp-filter-main--compact">
          <div>
            <label className="sp-label">Event type</label>
            <div className="sp-segment mt-2" role="tablist" aria-label="Event type filters">
              {EVENT_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  role="tab"
                  aria-selected={typeFilter === type}
                  className={typeFilter === type ? 'active' : ''}
                  onClick={() => setTypeFilter(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="sp-label">Risk</label>
            <div className="sp-segment mt-2" role="tablist" aria-label="Risk filters">
              {RISK_LEVELS.map((risk) => (
                <button
                  key={risk}
                  type="button"
                  role="tab"
                  aria-selected={riskFilter === risk}
                  className={riskFilter === risk ? 'active' : ''}
                  onClick={() => setRiskFilter(risk)}
                >
                  {risk}
                </button>
              ))}
            </div>
          </div>
          <div className="sp-filter-meta">
            <Badge variant="inactive">{filtered.length} events</Badge>
            {hasActiveFilters && (
              <Button size="sm" variant="ghost" type="button" onClick={() => {
                setTypeFilter('All');
                setRiskFilter('All');
              }}>
                Clear filters
              </Button>
            )}
          </div>
        </div>
        {hasActiveFilters && (
          <div className="sp-filter-pills">
            {activeFilters.map((filter) => (
              <span key={filter.id} className="sp-filter-pill">
                {filter.label}
                <button type="button" aria-label={`Remove ${filter.label}`} onClick={filter.onRemove}>
                  x
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm sp-muted">No events match the selected filters.</div>
      ) : (
        <div className="space-y-6">
          <TimelineGroup label="Today" count={todayItems.length}>
            {todayItems.length ? (
              renderGroupList(todayItems, false)
            ) : (
              <div className="text-sm sp-muted">No events today.</div>
            )}
          </TimelineGroup>

          <TimelineGroup label="This week" count={weekItems.length}>
            {buildGroupItems(weekItems, true).length ? (
              renderGroupList(weekItems, true)
            ) : (
              <div className="text-sm sp-muted">No events this week.</div>
            )}
          </TimelineGroup>

          <TimelineGroup
            label="Earlier"
            count={earlierItems.length}
            collapsible
            collapsed={!showEarlier}
            onToggle={() => setShowEarlier((prev) => !prev)}
          >
            {earlierItems.length ? (
              renderGroupList(earlierItems, false)
            ) : (
              <div className="text-sm sp-muted">No earlier events.</div>
            )}
          </TimelineGroup>
        </div>
      )}

      <div className="sp-panel sp-panel--muted p-3 text-xs sp-muted">
        Safeguarding signal logic: flag when 3+ unauthorised absences occur within 14 days (critical at 5+).
      </div>
    </div>
  );
}
